import { useEffect, useState, useCallback } from 'react'
import { applyHighlights, countWordsAndGetExcess } from './codemirror.helper'
import { isNil } from 'lodash'

export const useHighlights = (editorInstance, highlightRanges, focusedIndex = -1) => {
    useEffect(() => {
        if (editorInstance.current) {
            applyHighlights(editorInstance.current, highlightRanges, focusedIndex)
        }
    }, [highlightRanges, editorInstance, focusedIndex])
}

export const useExcessWordsHighlights = (editorInstance, value, wordsLimit) => {
    useEffect(() => {
        if (editorInstance.current) {
            const highlightExcessWords = (editor, text, wordsLimit) => {
                if (isNil(wordsLimit)) return
                const { excessStart } = countWordsAndGetExcess(text, wordsLimit)
                if (excessStart !== null) {
                    editor.getAllMarks().forEach(mark => (mark.className === 'excess-content' ? mark.clear() : null)) // Clear previous excess word marks
                    editor.markText(
                        // Mark excess word
                        editor.posFromIndex(excessStart),
                        { line: editor.lastLine() },
                        { className: 'excess-content' },
                    )
                }
            }

            highlightExcessWords(editorInstance.current, value, wordsLimit)
        }
    }, [editorInstance, wordsLimit, value])
}

export const useHighlightNavigation = (editorInstance, highlightRanges) => {
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1)
    const [isContentTooLong, setIsContentTooLong] = useState(false)
    const [highlightPositions, setHighlightPositions] = useState([])

    // Check if content fits vertically on screen
    const checkContentHeight = useCallback(() => {
        if (!editorInstance.current) return
        
        const editor = editorInstance.current
        // Add a small delay to ensure the editor has fully rendered
        setTimeout(() => {
            if (editorInstance.current) {
                const scrollInfo = editor.getScrollInfo()
                const hasVerticalScroll = scrollInfo.height > scrollInfo.clientHeight
                setIsContentTooLong(hasVerticalScroll)
            }
        }, 100)
    }, [editorInstance])

    // Calculate highlight positions
    useEffect(() => {
        if (!editorInstance.current || !highlightRanges) return
        
        const editor = editorInstance.current
        const positions = highlightRanges.map(highlight => {
            const startPos = editor.posFromIndex(highlight.start)
            const endPos = editor.posFromIndex(highlight.end)
            return { startPos, endPos, highlight }
        })
        
        setHighlightPositions(positions)
        checkContentHeight()
    }, [highlightRanges, editorInstance, checkContentHeight])

    // Additional check after editor mounts to ensure scroll detection works
    useEffect(() => {
        if (!editorInstance.current) return
        
        // Check again after a longer delay to catch late-rendered content
        const timeoutId = setTimeout(() => {
            checkContentHeight()
        }, 500)
        
        return () => clearTimeout(timeoutId)
    }, [editorInstance, checkContentHeight])

    // Scroll to highlight with offset
    const scrollToHighlight = useCallback((index) => {
        if (!editorInstance.current || !highlightPositions[index]) return
        
        const editor = editorInstance.current
        const { startPos } = highlightPositions[index]
        
        // Scroll with offset from top
        const scrollOffset = 100
        editor.scrollIntoView(startPos, scrollOffset)
        
        setCurrentHighlightIndex(index)
    }, [editorInstance, highlightPositions])

    const goToPreviousHighlight = useCallback(() => {
        if (currentHighlightIndex > 0) {
            scrollToHighlight(currentHighlightIndex - 1)
        }
    }, [currentHighlightIndex, scrollToHighlight])

    const goToNextHighlight = useCallback(() => {
        if (currentHighlightIndex < highlightPositions.length - 1) {
            scrollToHighlight(currentHighlightIndex + 1)
        }
    }, [currentHighlightIndex, highlightPositions.length, scrollToHighlight])

    const hasHighlights = highlightRanges && highlightRanges.length > 0
    const showNavigation = hasHighlights && isContentTooLong
    const canGoPrevious = currentHighlightIndex > 0
    const canGoNext = currentHighlightIndex < highlightPositions.length - 1

    return {
        showNavigation,
        canGoPrevious,
        canGoNext,
        currentHighlightIndex,
        totalHighlights: highlightPositions.length,
        goToPreviousHighlight,
        goToNextHighlight,
        scrollToHighlight,
        focusedHighlightIndex: currentHighlightIndex
    }
}
